<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.companyCode" placeholder="企业编号" clearable @keydown.enter="doSearch" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.companyId"
          filterable
          placeholder="企业名称"
          clearable
        >
          <el-option
            v-for="item in companyList"
            :key="item.id"
            :label="item.companyName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.agreementSigningType"
          filterable
          placeholder="待签署类型"
          clearable
        >
          <el-option
            v-for="item in signmentTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" class=" mr-[6px]" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" :max-height="maxHeight" :cell-style="initOperatorColumnStyle">
      <el-table-column align="left" type="index" label="序号" :min-width="80" />
      <el-table-column align="left" prop="companyCode" label="企业编号" :min-width="210" />
      <el-table-column align="left" prop="companyName" label="企业名称" show-overflow-tooltip :min-width="230" />
      <el-table-column align="left" prop="agentName" label="所属代理商" show-overflow-tooltip :min-width="230">
        <template #default="scope">
          <el-tag
            round
            size="small"
            type="primary"
            class="mr-1"
          >
            {{ agentLevelLabel[scope.row.agentLevel] }}
          </el-tag> {{ scope.row.agentName }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="channel" label="支付通道" :min-width="180">
        <template #default="{ row }">
          {{ payChannelLabel[row.channel] }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="agreementSigningType" label="签署类型" :min-width="200">
        <template #default="{ row }">
          {{ signmentTypeLabel[row.agreementSigningType] }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :class="{ 'fix-fixed-footer': fixedFooter }"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { initOperatorColumnStyle, SvgIcon, TableRowButtons } from '@vue3/components';
import { IEnterpriseItem } from '../../../../types';
import { agentLevelLabel, payChannelLabel } from '../../../../types/common';
import { IUnSignDetail, IUnSignQuery, signmentTypeLabel, signmentTypeOptions } from '../../../../types/air-splitting/paymentAccount';
import { ContentContainer, SearchContainer, SearchCol, useCardTableHeightFix, useTblPermissionButtons, queryEnterpriseList, queryUnSignList } from '../../../../index';

defineOptions({ name: 'GroupAudit' }); // 账户中心 - 集团中心 - 集团审核

const defaultPageSize = 12;

const params = reactive<IUnSignQuery>({
  page: 1,
  size: defaultPageSize,
});

const loading = ref(false);
const total = ref(0);
const tableData = ref<IUnSignDetail[]>([]);
const companyList = ref<IEnterpriseItem[]>([]);

const { maxHeight, fixedFooter } = useCardTableHeightFix(total);

const { buttons } = useTblPermissionButtons([
  {
    name: '签署',
    action: doSignment,
  },
]);

function doSignment(row: IUnSignDetail) {
  if (!row.agreementSignUrl) {
    ElMessage.error('未找到签署地址');
    return;
  }

  window.open(row.agreementSignUrl);
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryUnSignList(params);

    const dom = document.getElementById('audit-num');

    if (params.page === 1 && dom) {
      dom.innerText = String(all);
    }

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

async function fetchCompanyList() {
  try {
    const { data } = await queryEnterpriseList();

    companyList.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

fetchData();
fetchCompanyList();
</script>

<style lang="scss" scoped>
.content-wrapper-with-tabs {
  .fix-fixed-footer {
    @apply absolute bottom-[15px] w-[calc(100%-49px)];
  }
}
</style>
